/**
 * <s-buy-assist> wraps the product buy box. It adds two things on top of the
 * existing product form without replacing any of its logic:
 *
 * 1. Explicit size choice. When the product has a size option with more than
 *    one value (`[data-size-option]`), sizes start unselected on page load
 *    (unless the URL already names a variant). Until the shopper picks a size,
 *    the Add to Basket button reads "Choose size", submitting focuses the size
 *    picker instead of adding, and the express checkout buttons are inert.
 *    Picking a size fires the normal variant-picker change, so the existing
 *    partial render sets the correct variant id and button state.
 *
 * 2. A compact sticky purchase bar (`[data-buy-bar]`, mobile only via CSS)
 *    that appears once the main product form has scrolled above the viewport.
 *    It mirrors the current price and size, and its button either jumps to
 *    the size picker or clicks the real Add to Basket button, so the add goes
 *    through the same form, variant id and cart flow.
 *
 * Without JavaScript the native form works unchanged.
 */
class BuyAssist extends HTMLElement {
  #ac = null;
  #chosen = true;
  #observer = null;
  #mutations = null;
  #barVisible = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const needsSize = this.dataset.sizeRequired === 'true';
    const urlHasVariant = new URL(location.href).searchParams.has('variant');
    this.#chosen = !needsSize || urlHasVariant;

    if (!this.#chosen) this.#clearSizeSelection();

    // Mark the size as chosen once the shopper changes the size option.
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-size-option]')) {
        this.#chosen = true;
        this.#hidePrompt();
        this.#apply();
      }
    }, opts);

    // Capture phase runs before the product form's own submit handler.
    this.addEventListener('submit', (event) => {
      if (this.#chosen) return;
      event.preventDefault();
      event.stopPropagation();
      this.#promptForSize();
    }, { capture: true, signal: this.#ac.signal });

    this.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-buy-bar-action]') : null;
      if (!trigger) return;
      event.preventDefault();
      if (!this.#chosen) {
        this.#promptForSize();
        return;
      }
      const addButton = this.querySelector('[data-product-form] button[name="add"]');
      if (addButton && addButton.getAttribute('aria-disabled') !== 'true') addButton.click();
    }, opts);

    // The product form partial is replaced after each variant change; re-apply state to the new markup.
    const form = this.querySelector('[data-product-form]');
    if (form) {
      this.#mutations = new MutationObserver(() => this.#apply());
      this.#mutations.observe(form, { childList: true, subtree: true });

      this.#observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.#setBarVisible(scrolledPast);
      });
      this.#observer.observe(form);
    }

    this.#apply();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#mutations?.disconnect();
    this.#setBarVisible(false);
  }

  #clearSizeSelection() {
    this.querySelectorAll('[data-size-option] input[type="radio"]:checked').forEach((input) => {
      input.checked = false;
    });
  }

  #selectedSize() {
    const checked = this.querySelector('[data-size-option] input[type="radio"]:checked');
    if (checked) return checked.value;
    const select = this.querySelector('[data-size-option] select');
    return select ? select.value : '';
  }

  #apply() {
    // Pause observation while we write, so our own edits do not retrigger it.
    this.#mutations?.disconnect();

    const addButton = this.querySelector('[data-product-form] button[name="add"]');
    const soldOutButton = this.querySelector('[data-product-form] button[aria-disabled="true"]:not([name="add"])');
    const payment = this.querySelector('[data-payment-buttons]');

    if (addButton) {
      if (!addButton.dataset.label) addButton.dataset.label = addButton.textContent.trim();
      const label = this.#chosen ? addButton.dataset.label : this.dataset.chooseLabel;
      if (addButton.textContent.trim() !== label) addButton.textContent = label;
    }
    if (payment) payment.inert = !this.#chosen;
    this.toggleAttribute('data-size-pending', !this.#chosen);

    // Sticky bar mirrors price, size and availability.
    const barPrice = this.querySelector('[data-buy-bar-price]');
    const price = this.querySelector('[data-variant-price]');
    if (barPrice && price) {
      const text = price.textContent.trim();
      if (barPrice.textContent !== text) barPrice.textContent = text;
    }

    const barSize = this.querySelector('[data-buy-bar-size]');
    const size = this.#selectedSize();
    if (barSize) {
      const text = this.#chosen && size ? this.dataset.sizeLabel.replace('__SIZE__', size) : '';
      if (barSize.textContent !== text) barSize.textContent = text;
      barSize.hidden = text === '';
    }

    const action = this.querySelector('[data-buy-bar-action]');
    if (action) {
      let label;
      let disabled = false;
      if (!this.#chosen) {
        label = this.dataset.chooseLabel;
      } else if (addButton) {
        label = addButton.dataset.label || addButton.textContent.trim();
      } else {
        label = soldOutButton ? soldOutButton.textContent.trim() : this.dataset.soldOutLabel;
        disabled = true;
      }
      if (action.textContent !== label) action.textContent = label;
      action.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      action.classList.toggle('opacity-50', disabled);
    }

    const form = this.querySelector('[data-product-form]');
    if (form && this.#mutations) this.#mutations.observe(form, { childList: true, subtree: true });
  }

  #promptForSize() {
    const prompt = this.querySelector('[data-size-prompt]');
    if (prompt) prompt.hidden = false;
    const fieldset = this.querySelector('[data-size-option]');
    if (!fieldset) return;
    fieldset.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    const first = fieldset.querySelector('input[type="radio"]:not([disabled]), select');
    first?.focus({ preventScroll: true });
  }

  #hidePrompt() {
    const prompt = this.querySelector('[data-size-prompt]');
    if (prompt) prompt.hidden = true;
  }

  #setBarVisible(visible) {
    const bar = this.querySelector('[data-buy-bar]');
    if (!bar || visible === this.#barVisible) return;
    this.#barVisible = visible;
    bar.toggleAttribute('data-visible', visible);
    bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    bar.inert = !visible;
    // Reserve space at the end of the page so the bar never covers the last content (footer links, policies).
    document.body.style.paddingBottom = visible ? `${bar.offsetHeight}px` : '';
  }
}

if (!customElements.get('s-buy-assist')) customElements.define('s-buy-assist', BuyAssist);
